<script setup>
import { onMounted, ref, reactive } from "vue";
import axios from "../axios";
import store from "../store";
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";

const url = ref(process.env.VUE_APP_API_URL);

const QuoteArray = ref([]);
const isLoading = ref(true);

const connectMicrosoft = () => {
  axios.post("api/microsoft/auth-url").then((response) => {
    const authUrl = response.data.url;
    const popup = window.open(authUrl, 'MicrosoftLogin', 'width=600,height=600')
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      alert("Your browser blocked the popup. Please allow popups for this site and try again.");
      return;
    }
    window.addEventListener('message', async (event) => {
      if (event.data?.type === 'MICROSOFT_CONNECTED') {
        const { success } = event.data;
        if (success) {
          store.commit('setSuccessNotification', "Microsoft 365 connected successfully");
          store.commit('setErrorNotification', null);
          setTimeout(() => {
            store.commit('clearNotifications');
          }, 3000);
          store.dispatch('getUser');
          popup.close();
        }
      }
    }, { once: true })

  }).catch((error) => {
    store.commit('setErrorNotification', "Error connecting to Microsoft 365. Please try again.");
    store.commit('setSuccessNotification', null);
    setTimeout(() => {
      store.commit('clearNotifications');
    }, 3000);
    console.log("Error connecting to Microsoft 365:", error);
  });
};

const goToPage = (url) => {
  const page = new URL(url).searchParams.get('page');
  fetchAllPdfs(Number(page));
}

const pagination = reactive({
  current_page: 1,
  last_page: 1,
  links: [],
})


// Get Product
const lastPage = ref(1);
const currentPage = ref(1);
const fetchAllPdfs = async (page = 1) => {
  try {
    currentPage.value = page;
    const response = await axios.get(url.value + 'api/allPdf', {
      params: {
        page: page
      }
    });
    QuoteArray.value = response.data.data;
    lastPage.value = response.data.last_page;
    Object.assign(pagination, response.data)
    isLoading.value = false;
  } catch (error) {
    isLoading.value = false;
    console.error('Error fetching PDFs:', error.response?.data || error.message);
  }
};

const quoteDeleteId = ref(null);
const showProceedModel = ref(false);
const deleteQuote = (id)=>{
    quoteDeleteId.value = id;
    showProceedModel.value = true;
}
const confirmDeleteQuote = async () => {
    try {
        const response = await axios.post(url.value + 'api/deleteQuote' ,{id:quoteDeleteId.value});
        store.commit('setSuccessNotification', "Quote Delete successfully");
        store.commit('setErrorNotification', null);
        setTimeout(() => {
        store.commit('clearNotifications');
        }, 3000);
        showProceedModel.value = false;
        console.log('Delete success:', response.data);
        fetchAllPdfs();
    } catch (error) {
        showProceedModel.value = false;
        console.error('Delete error:', error.response?.data || error.message);
        alert(error.response?.data?.error || 'Failed to delete PDF.');
        store.commit('setErrorNotification', error.response?.data?.error || 'Failed to delete PDF.');
        store.commit('setSuccessNotification', null);
        setTimeout(() => {
        store.commit('clearNotifications');
        }, 3000);
    }
};

const notifications = ref([]);
const getNotifications = async () => {
  axios.post("api/getNotificationDashboard").then((response) => {
    notifications.value = response.data;
    isLoading.value = false;
  }).catch((error) => {
    isLoading.value = false;
    console.error("Error fetching notifications:", error);
  });
};

const markAsRead = (id) => {
  axios.post("api/markNotificationAsRead", { id: id }).then(() => {
    getNotifications();
  }).catch(err => console.error(err));
};
const encode = (id) => {
  return encodeURIComponent(btoa(id.toString()));
};

onMounted(() => {
  fetchAllPdfs();
  getNotifications();
});

</script>
<template>
  <div class="py-4 container-fluid ctm-mobile-padding">
    <div class="row">
      <div class="col-lg-12">
        <div class="card p-3 p-md-5 shadow-lg rounded-xl bg-light-color text-gray-800 position-relative">
                    <div class="ctm-loader" v-if="isLoading">
                        <div class="loader" id="loader-5">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>

        <div class="row mb-3">
          <div class="col-lg-3 col-md-6 col-12 px-2 mobile-card">
            <mini-statistics-card class="h-100" title="Total Quote Generated" :value="store.state.auth.totalQuotes ?? 0"
              :icon="{
                component: 'ni ni-money-coins',
                background: 'bg-gradient-primary',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12 px-2 mobile-card">
            <mini-statistics-card class="h-100" title="Pending Quote Approval"
              :value="store.state.auth.pendingQuotes ?? 0" :icon="{
                component: 'ni ni-world',
                background: 'bg-gradient-danger',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12 px-2 mobile-card">
            <mini-statistics-card class="h-100" title="Quote Send to Customer" :value="store.state.auth.sendQuotes ?? 0"
              :icon="{
                component: 'ni ni-paper-diploma',
                background: 'bg-gradient-success',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12 ctm-fed-card px-2 mobile-card">
            <mini-statistics-card class="h-100" title="UPS API Status" value="30" description="<span
                class='text-sm font-weight-bolder text-success'
                >Active</span>" :icon="{
                  component: 'ni ni-cart',
                  background: 'bg-gradient-warning',
                  shape: 'rounded-circle',
                }" />
          </div>
        </div>
        <div class="row g-4">

          <div class="col-md-6 px-1">
            <div class="status-card h-100">
              <div class="row align-items-center position-relative">
                <span class="conevt-badge" v-if="store.state.auth.user?.status">{{ store.state.auth.user?.status
                }}</span>
                <div class="col-md-8">
                  <h6 class="text-color">Connected Microsoft 365 Email ID</h6>
                  <p>{{ store.state.auth.user?.microsoft_email }}</p>
                  <button class="btn btn-bg text-white mb-0" @click="connectMicrosoft">Connect With Microsoft</button>
                </div>
                <div class="col-md-4 microdoft-image position-relative">
                  <img src="@/assets/img/microsoft-logo.png" class="img-fluid">
                  <!-- <span class="conevt-badge">Connected</span> -->
                </div>
              </div>
            </div>
          </div>


          <div class="col-md-6 px-1">
            <div class="status-card">
              <h6 class="text-color">Notifications</h6>
              <div v-for="(notification, index) in notifications" :key="index">
                <div class="notification-item "
                  :class="notification.read_at ? 'notification-read' : 'notification-unread'">
                  <router-link :to="`/single-quotation/${encode(notification.data.q_id)}`"
                    @click="markAsRead(notification.id)">🔔{{ notification.data.message
                    }}</router-link>
                </div>
              </div>
              <div class="notification-item" v-if="notifications.length == 0">No Notification.</div>
              <router-link to="/notifications" class="btn btn-bg text-white mb-0 mt-2">View All</router-link>
            </div>
          </div>
        </div>


        <div class="row mt-4">
          <div class="col-lg-12 mb-lg-0 mb-4">
            <div class="card">
              <div class="p-3 pb-0 card-header">
                <div class="d-flex justify-content-between">
                  <h6 class="mb-2">Quote Requests</h6>
                </div>
              </div>
              <div class="table-overflow">
                <table class="table align-items-center w-100">
                  <thead>
                    <tr>
                      <th class="text-start px-3">Quotation No</th>
                      <th class="text-start px-2">Customer Name</th>
                      <th class="text-start px-2">Date</th>
                      <!-- <th class="text-start px-2">Item</th> -->
                      <th class="text-start px-2">Shipping Address</th>
                      <th class="text-start px-2">Status</th>
                      <th class="text-start px-2">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="QuoteArray.length > 0">
                    <tr v-for="(quotemanegement, index) in QuoteArray" :key="index">
                      <td class="fs-14 ps-3">#{{ quotemanegement.quotation_number }}</td>
                      <td class="fs-14">{{ quotemanegement.customer_name }}</td>
                      <td class="fs-14">{{ quotemanegement.shipment_date }}</td>
                      <td class="fs-14">{{ quotemanegement.ship_to_address }},{{ quotemanegement.ship_to_cityzip }} {{
                        quotemanegement.ship_to_country }}</td> <!-- Static Address -->
                      <td class="fs-14">
                        <span class="status-badge" :class="{
                          'bg-pending': quotemanegement.status == 'Pending',
                          'bg-active': quotemanegement.status == 'Approved'
                        }">
                          {{ quotemanegement.status }}
                        </span>
                      </td>
                      <td class="fs-14">
                          <div class="d-flex align-items-center gap-2">

                        <router-link class="ms-2" :to="`/single-quotation/${encode(quotemanegement.id)}`"><svg
                            xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#a35c2e" class="bi bi-eye"
                            viewBox="0 0 16 16">
                            <path
                              d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                            <path
                              d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                          </svg> </router-link>
                           <button class="delete-notification"
                              @click="deleteQuote(quotemanegement.id)"><svg
                                  xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                  fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
                                  <path
                                      d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                            </svg>
                          </button>
                          </div>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="6" class="text-center">No data available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="peginationMain">
                <nav class="pagination" v-if="lastPage > 1">
                  <a href="#" class="previous" :class="{ disabled: pagination.current_page === 1 }"
                    @click.prevent="pagination.current_page !== 1 && goToPage(pagination.links[0].url)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd"
                        d="M18 10a.75.75 0 0 1-.75.75H4.66l2.1 1.95a.75.75 0 1 1-1.02 1.1l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 1 1 1.02 1.1l-2.1 1.95h12.59A.75.75 0 0 1 18 10Z"
                        clip-rule="evenodd" />
                    </svg>
                    Previous
                  </a>
                  <ul class="pages">
                    <li v-for="(link, index) in pagination.links.slice(1, -1)" :key="index" class="page">
                      <a href="#" v-if="link.url" :class="{ current: link.active }" @click.prevent="goToPage(link.url)"
                        v-html="link.label"></a>
                      <span v-else-if="link.label === '...'" class="dots">…</span>
                    </li>
                  </ul>
                  <a href="#" class="next" :class="{ disabled: pagination.current_page === pagination.last_page }"
                    @click.prevent="pagination.current_page !== pagination.last_page && goToPage(pagination.links[pagination.links.length - 1].url)">
                    Next
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd"
                        d="M18 10a.75.75 0 0 1-.75.75H4.66l2.1 1.95a.75.75 0 1 1-1.02 1.1l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 1 1 1.02 1.1l-2.1 1.95h12.59A.75.75 0 0 1 18 10Z"
                        clip-rule="evenodd" />
                    </svg>
                  </a>
                </nav>
              </div>


            </div>
          </div>

        </div>
      </div>
      </div>
    </div>
  </div>

      <div v-if="showProceedModel" class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h5 class="modal-title">Confirmation</h5>
                <button class="close-button" @click="showProceedModel = false">&times;</button>
            </div>
            <div class="modal-body">
                <p class="mb-0">are you sure you want to delete a quotation.?</p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-bg-yellow" @click="showProceedModel = false">No</button>
                <button class="btn btn-bg" @click="confirmDeleteQuote">Yes</button>
            </div>
        </div>
    </div>
</template>
<style>
.ctm-fed-card h5 {
  display: none;
}

.ctm-fed-card p.mt-2 {
  margin-top: 0px !important;
}

.status-card {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 20px;
  background-color: white;
}

.btn-blue {
  background-color: #1a73e8 !important;
  color: #fff !important;
  border: none;
}

.btn-blue:hover {
  background-color: #1558b0;
}

.notification-item {
  font-size: 14px;
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}

.fs-14 {
  font-size: 14px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  display: inline-block;
}

.bg-pending {
  background-color: #ffeeba;
  color: #856404;
}

.bg-active {
  background-color: #d4edda;
  color: #155724;
}

.bg-success {
  background-color: #7b5540 !important;
}



.peginationMain {
  padding: 40px 0px;
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}


.pagination {
  display: flex;
  width: 100%;
  height: 40px;
  font-family: "Inter", sans-serif;
  color: #6b7280;
  justify-content: space-between;
  border-top: 1px solid #e5e7eb;
}

.pagination>a:last-of-type {
  text-align: right;
}

.pagination a,
.pagination .dots {
  color: #000;
  display: block;
  padding: 16px 16px 0;
  text-decoration: none;
  margin-top: -1px;
  border-top: 2px solid transparent;
}

.pagination .dots {
  color: #000;
  font-weight: 500;
}

.pagination a:hover {
  border-color: #d1d5db;
  color: #374151;
}

.pagination a:active,
.pagination a.current {
  border-color: #f59e0b;
  color: #374151;
}

.pagination>a {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.pagination>a:first-of-type {
  padding: 16px 4px 0 0;
}

.pagination>a:last-of-type {
  padding: 16px 0 0 4px;
}

.pagination>a>svg {
  width: 1.25rem;
  height: 1.25rem;
}

.pagination>a:first-of-type>svg {
  margin-right: 20px;
}

.pagination>a:last-of-type>svg {
  margin-left: 20px;
}

.pagination>a.next>svg {
  transform: scaleX(-1);
}

.pagination .pages {
  display: flex;
  width: 50%;
  max-width: 468px;
  margin: 0;
  padding: 0;
  list-style-type: none;
  justify-content: space-evenly;
}

.pagination a.disabled {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
  border-color: transparent !important;
  color: #9ca3af;
}

.pagination a.disabled svg {
  pointer-events: none;
  opacity: 0.5;
}


.notification-unread {
  border-radius: 10px !important;
  background-color: #f2e5d7 !important;
  overflow: hidden;
}

.notification-read {
  border-radius: 10px !important;
  background-color: #fffaf4 !important;
  overflow: hidden;
}

@media (max-width: 735px) {
  .pagination .pages {
    display: none;
  }
}


/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-container {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    margin-top: 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: none !important;
}

.close-button {
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
}
</style>
